/* global React */
/* eslint-disable no-restricted-globals,no-nested-ternary */
import { Box, lighten, useTheme } from '@mui/material'
import { classNames, getThemeColor, isNum, isObj } from 'lib/util'
import { useMemoRef, useStyles } from 'platform/react/hook'
import { type ImageIcon } from 'ui/Element/Image'
import { type Ref } from 'react'
import { type View } from 'react-native'

/** the shape of the icon's box */
export type IconShape = 'circle' | 'rounded' | null

export interface IconStyleProps {
  /** background color of the icon's box */
  background?: string
  /** color of the icon */
  color?: string
  /** shape of the icon's box */
  shape: IconShape
  /** padding around the icon */
  padding?: number | string
  /** angle to rotate the icon */
  rotate?: number | string
}

// Props of the platform Icon elements
export interface IconProps extends ImageIcon {
  /** the shape of the icon's box */
  shape?: IconShape
  /** a css class for the root element (web only) */
  className?: string
  /** style overrides for the icon (mobile only) */
  style?: object
  /** style overrides for the icon container (mobile only) */
  containerStyle?: object
  /** a reference to the root element (mobile only) */
  ref?: Ref<View>
}

/**
 * Returns the icon element's current theme styles.
 *
 * @param theme            the application's current theme
 * @param props            the element's properties
 * @param props.background background color of the icon's box
 * @param props.color      color of the icon
 * @param props.shape      shape of the icon's box
 * @param props.padding    padding around the icon
 * @param props.rotate     angle to rotate the icon
 * @returns                the icon's styles
 */
const styles = (theme: any, { background, color, shape, padding, rotate }: IconStyleProps) => ({
  root: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-around',
    backgroundColor: (background && getThemeColor(theme, background)) || (color
      ? lighten(getThemeColor(theme, color) || '', 0.9)
      : theme.palette.background.selected),
    ...shape === 'circle' && {
      borderRadius: '50%',
    },
    ...shape === 'rounded' && {
      borderRadius: '0.25rem',
    },
    padding: ((padding || padding === 0) && isNum(padding) && theme.spacing(padding))
      || padding
      || '0.75rem',
  },
  icon: {
    ...rotate && {
      transform: `rotate(${rotate}deg)`,
    },
    maskSize: 'contain',
    maskPosition: 'center',
    maskRepeat: 'no-repeat',
  },
  ...theme.custom.icon,
})

/**
 * Icon Element
 *
 * Displays a custom SVG icon, masked with `color` and optionally contained
 * in a `shape`d box.
 * https://caniuse.com/?search=mask
 *
 * @param props              the element's properties
 * @param props.ref          a reference to the root element
 * @param props.name         name of the svg icon
 * @param props.source       higher priority alternative configuration
 * @param props.size         size of the icon
 * @param props.color        color of the icon
 * @param props.background   background color of the icon's box
 * @param props.shape        shape of the icon's box, `null` means no box
 * @param props.padding      padding around the icon
 * @param props.iconPadding  alias for `padding` (padding may get swallowed by @{link Section})
 * @param props.rotate       angle to rotate the icon
 * @param props.className    external styling
 * @returns                  the new icon element
 */
const Icon = ({
  ref,
  className,
  iconPadding,
  source,
  ...props
}: IconProps & { iconPadding?: number | string }) => {
  const sourceObj = isObj(source) ? source as IconProps : {}
  const {
    name: iconName = (source as string) || 'generic',
    size = 24,
    color,
    background,
    shape = null,
    rotate,
    padding = iconPadding,
  } = { ...props, ...sourceObj }

  const theme = useTheme() as any
  const classes = useStyles(styles, {
    background, color, shape, padding, rotate,
  })() as Record<string, string>

  const src = iconName ? `assets/icon/${iconName}.svg` : null

  const icon = !src ? null : (
    <Box
      ref={ref}
      className={classNames(classes.icon, className || '')}
      sx={{
        width: size,
        height: size,
        maskImage: `url(${src})`,
        backgroundColor: (color && getThemeColor(theme, color, true))
          || theme.palette.black.main,
      }}
    />
  )

  return !icon ? null : shape ? (
    <Box className={classes.root}>
      {icon}
    </Box>
  ) : icon
}

/**
 * FileIcon Element
 *
 * Displays the `file-` prefixed icon for a file-type `name`, e.g. `pdf`.
 *
 * @param props      the element's properties
 * @param props.name name of the file type
 * @returns          the new file icon element
 */
export const FileIcon = ({ ...props }: IconProps) => Icon({
  ...props,
  name: `file-${props.name}`,
})

export default useMemoRef(Icon, (props: IconProps) => [
  props.source,
  props.name,
  props.color,
  props.background,
  props.shape,
  props.rotate,
  props.className,
])
